import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast } from "@/hooks/use-toast";

const PushNotificationToggle = () => {
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    loading, 
    subscribe, 
    unsubscribe 
  } = usePushNotifications();
  
  const [showPermissionHint, setShowPermissionHint] = useState(false);

  useEffect(() => {
    // Show hint if permission was denied
    if (permission === "denied") {
      setShowPermissionHint(true);
    }
  }, [permission]);

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      const result = await subscribe();
      if (result.success) {
        toast({
          title: "🔔 Notifications Enabled",
          description: "You'll get alerts when someone likes or pops you!"
        });
      } else if (result.error === "Permission denied") {
        setShowPermissionHint(true);
        toast({
          title: "Permission Required",
          description: "Please enable notifications in your browser settings",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to enable notifications",
          variant: "destructive"
        });
      }
    } else {
      const result = await unsubscribe();
      if (result.success) {
        toast({
          title: "Notifications Disabled",
          description: "You won't receive push notifications"
        });
      }
    }
  };

  if (!isSupported) {
    return (
      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
        <div className="flex items-center gap-3">
          <BellOff className="w-5 h-5 text-muted-foreground" />
          <div>
            <Label className="text-base">Push Notifications</Label>
            <p className="text-xs text-muted-foreground">
              Not supported in this browser
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between p-4 rounded-xl bg-card border">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${isSubscribed ? 'bg-primary/10' : 'bg-muted'}`}>
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            ) : isSubscribed ? (
              <Bell className="w-5 h-5 text-primary" />
            ) : (
              <BellOff className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
          <div>
            <Label htmlFor="push-toggle" className="text-base cursor-pointer">
              Push Notifications
            </Label>
            <p className="text-xs text-muted-foreground">
              {isSubscribed 
                ? "You'll receive real-time alerts" 
                : "Enable to get notified instantly"
              }
            </p>
          </div>
        </div>
        <Switch
          id="push-toggle"
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={loading}
        />
      </div>

      {showPermissionHint && permission === "denied" && (
        <div className="p-3 rounded-lg bg-destructive/10 text-sm text-destructive">
          <p className="font-medium">Notifications blocked</p>
          <p className="text-xs mt-1">
            To enable notifications, click the lock icon in your browser's address bar 
            and allow notifications for this site.
          </p>
        </div>
      )}
    </div>
  );
};

export default PushNotificationToggle;
